import { handleRetrieveMemberAppointments } from "../../../../redux/member-slices/appointment.slice"
import { showDialogWithData } from "../../../../redux/guest-slices/other.slice"
import TablePagination from "../../../../components/common/TablePagination"
import { debounce, FILE_PATH_URI } from "../../../../utils/helper"
import TableSkell from "../../../../components/common/TableSkell"
import InputFieldB from "../../../../components/ui/InputFieldB"
import { CANCEL_APPOINTMENT } from "../../../../utils/keywords"
import { chipVariants, pendingAppoHeaders } from "./data"
import { useDispatch, useSelector } from "react-redux"
import { useToast } from "../../../../hooks/useToast"
import { useEffect, useRef, useState } from "react"
import Icons from "../../../../assets/js/Icons"
import moment from "moment"

import {
  Avatar, Box, Button, Chip, Grid2 as Grid, IconButton, ListItemText,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Typography,
} from "@mui/material"


function Pending() {
  const toast = useToast()
  const dispatch = useDispatch()
  const searchRef = useRef(null)
  const endDateRef = useRef(null)
  const startDateRef = useRef(null)
  const [data, setData] = useState({})
  const { loadings, refresh } = useSelector((state) => state.otherSlice)
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const formatTime = (utc) => moment(utc).tz(userTimeZone).format("h:mm A")
  const formatDate = (utc) =>
    moment(utc).tz(userTimeZone).format("MM-DD-YYYY")

  // --------------- Get all appointments ---------------
  const handleRetrieveAll = async (filters) => {
    const query = {
      limit: 10,
      type: "pending",
      timeZone: userTimeZone,
      currentPage: filters?.currentPage || 1,
      search: searchRef?.current?.value || null,
      endDate: endDateRef?.current?.value || null,
      startDate: startDateRef?.current?.value || null,
    }

    const response =
      (await dispatch(handleRetrieveMemberAppointments(query)).unwrap()) || {}
    if (response?.appointments) setData(response)
  }

  // --------------- Handle date filters ---------------
  const handleDateSearch = () => {
    const startDate = startDateRef?.current?.value
    const endDate = endDateRef?.current?.value

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      return toast.error("Start date cannot be later than end date")
    }
    handleRetrieveAll()
  }

  // --------------- Handle search ---------------
  const handleSearchChange = debounce(() => {
    handleRetrieveAll()
  }, 1000)

  // --------------- Handle reject appointment ---------------
  const handleReject = async (appointment) => {
    await dispatch(
      showDialogWithData({
        type: CANCEL_APPOINTMENT,
        data: {
          appointmentId: appointment?._id,
          startTime: appointment?.startTime,
        },
      })
    )
  }

  useEffect(() => {
    handleRetrieveAll()
    // eslint-disable-next-line
  }, [refresh])

  return (
    <>
      <Box py={5}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: "auto" }}>
            <InputFieldB
              type="text"
              name="search"
              variant="outlined"
              inputRef={searchRef}
              onChange={handleSearchChange}
              placeholder="Search..."
            />
          </Grid>

          <Grid container size={{ xs: 12, sm: "auto" }} spacing={2}>
            <Grid size={{ xs: 12, sm: "auto" }}>
              <InputFieldB
                type="date"
                name="startDate"
                variant="outlined"
                inputRef={startDateRef}
                placeholder="Start Date"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: "auto" }}>
              <InputFieldB
                type="date"
                name="endDate"
                variant="outlined"
                inputRef={endDateRef}
                placeholder="End Date"
              />
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, sm: "auto" }}>
            <Button
              fullWidth
              size="small"
              color="primary"
              variant="contained"
              onClick={handleDateSearch}
              sx={{ height: "100%" }}
            >
              Apply
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Paper variant="contentListing">
        <TableContainer variant="tableViewA">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {pendingAppoHeaders?.map((element, i) => (
                  <TableCell key={i} align="left">
                    {element}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loadings?.retrieveAll ? (
                <TableSkell colLength={pendingAppoHeaders?.length} />
              ) : (
                data?.appointments?.map((element, i) => (
                  <TableRow
                    key={i}
                    sx={{
                      td: {
                        borderBottomWidth:
                          i === data?.appointments?.length - 1 ? "0" : "1px",
                      },
                    }}
                  >
                    <TableCell>
                      <Avatar
                        sx={{ height: 35, width: 35 }}
                        src={FILE_PATH_URI + element?.specialistInfo?.image}
                      />
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>
                      {element?.specialistInfo?.name}
                    </TableCell>
                    <TableCell>
                      <ListItemText
                        sx={{ m: 0 }}
                        primary={formatDate(element?.startTime)}
                        secondary={
                          formatTime(element?.startTime) +
                          "-" +
                          formatTime(element?.endTime)
                        }
                        slotProps={{
                          primary: {
                            style: { fontSize: 15, lineHeight: "1.2" },
                          },
                          secondary: {
                            style: { fontSize: 13, lineHeight: "1.2" },
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>{formatDate(element?.createdAt)}</TableCell>
                    <TableCell>
                      <Chip
                        key={i}
                        size="small"
                        variant="statusChip"
                        color={chipVariants[element?.appointmentStatus]}
                        label={
                          element?.appointmentStatus === "no-show"
                            ? "No Show"
                            : element?.appointmentStatus
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {element?.appointmentStatus === "pending" && (
                        <>
                          <IconButton
                            size="small"
                            color="error"
                            variant="fill"
                            title="Cancel"
                            sx={{ p: 0.25, fontSize: 20 }}
                            onClick={() => handleReject(element)}
                          >
                            <Icons.Cross />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}

              {!data?.appointments?.length && !loadings?.retrieveAll && (
                <TableRow sx={{ td: { borderBottom: "none" } }}>
                  <TableCell colSpan={pendingAppoHeaders?.length}>
                    <Typography variant="noRecords">
                      No results found!
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          mt={0.001}
          color="primary"
          cb={handleRetrieveAll}
          pagination={data?.pagination}
          loading={loadings?.retrieveAll}
          length={data?.appointments?.length}
        />
      </Paper>
    </>
  )
}

export default Pending
